import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { UserProfile, Patient, Appointment, CallPanel } from '../types';

// Dados iniciais para popular o banco
const initialUsers: Omit<UserProfile, 'id'>[] = [
  {
    email: 'admin@sistema.com',
    name: 'Administrador do Sistema',
    role: 'admin',
    cpf: '000.000.000-00',
    cns: '000000000000000',
    phone: '(11) 99999-9999',
    position: 'Administrador Geral',
    registrationNumber: 'ADM001',
    unit: 'Administração',
    admissionDate: '2024-01-01T00:00:00.000Z',
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    email: 'coordenador@sistema.com',
    name: 'Dr. João Silva',
    role: 'coordenador',
    cpf: '111.111.111-11',
    cns: '111111111111111',
    phone: '(11) 98888-8888',
    position: 'Coordenador Médico',
    registrationNumber: 'CRM12345',
    unit: 'Coordenação',
    admissionDate: '2024-01-01T00:00:00.000Z',
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    email: 'medico@sistema.com',
    name: 'Dra. Maria Santos',
    role: 'medico',
    cpf: '222.222.222-22',
    cns: '222222222222222',
    phone: '(11) 97777-7777',
    position: 'Médica Clínica Geral',
    registrationNumber: 'CRM54321',
    unit: 'Ambulatório',
    admissionDate: '2024-01-01T00:00:00.000Z',
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    email: 'enfermeiro@sistema.com',
    name: 'Ana Paula Costa',
    role: 'enfermeiro',
    cpf: '333.333.333-33',
    cns: '333333333333333',
    phone: '(11) 96666-6666',
    position: 'Enfermeira Chefe',
    registrationNumber: 'COREN12345',
    unit: 'Ambulatório',
    admissionDate: '2024-01-01T00:00:00.000Z',
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    email: 'psicologo@sistema.com',
    name: 'Carlos Eduardo Lima',
    role: 'psicologo',
    cpf: '444.444.444-44',
    cns: '444444444444444',
    phone: '(11) 95555-5555',
    position: 'Psicólogo Clínico',
    registrationNumber: 'CRP12345',
    unit: 'Saúde Mental',
    admissionDate: '2024-01-01T00:00:00.000Z',
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    email: 'recepcionista@sistema.com',
    name: 'Fernanda Oliveira',
    role: 'recepcionista',
    cpf: '555.555.555-55',
    cns: '555555555555555',
    phone: '(11) 94444-4444',
    position: 'Recepcionista',
    registrationNumber: 'REC001',
    unit: 'Recepção',
    admissionDate: '2024-01-01T00:00:00.000Z',
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

const initialPatients: Omit<Patient, 'id'>[] = [
  {
    name: 'José da Silva',
    cpf: '123.456.789-00',
    rg: '12.345.678-9',
    cns: '123456789012345',
    birthDate: '1980-05-15T00:00:00.000Z',
    gender: 'masculino',
    phone: '(11) 91234-5678',
    email: 'jose.silva@email.com',
    address: {
      street: 'Rua das Flores',
      number: '123',
      complement: 'Apto 45',
      neighborhood: 'Centro',
      city: 'São Paulo',
      state: 'SP',
      zipCode: '01234-567'
    },
    emergencyContact: {
      name: 'Maria da Silva',
      relationship: 'Esposa',
      phone: '(11) 98765-4321'
    },
    medicalInfo: {
      allergies: ['Penicilina'],
      medications: ['Losartana 50mg'],
      conditions: ['Hipertensão'],
      bloodType: 'O+'
    },
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'system'
  },
  {
    name: 'Ana Carolina Souza',
    cpf: '987.654.321-00',
    rg: '98.765.432-1',
    cns: '987654321098765',
    birthDate: '1992-08-22T00:00:00.000Z',
    gender: 'feminino',
    phone: '(11) 99876-5432',
    email: 'ana.souza@email.com',
    address: {
      street: 'Avenida Paulista',
      number: '1000',
      neighborhood: 'Bela Vista',
      city: 'São Paulo',
      state: 'SP',
      zipCode: '01310-100'
    },
    emergencyContact: {
      name: 'Carlos Souza',
      relationship: 'Pai',
      phone: '(11) 97654-3210'
    },
    medicalInfo: {
      allergies: [],
      medications: [],
      conditions: [],
      bloodType: 'A+'
    },
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'system'
  },
  {
    name: 'Roberto Santos',
    cpf: '456.789.123-00',
    rg: '45.678.912-3',
    cns: '456789123456789',
    birthDate: '1975-12-10T00:00:00.000Z',
    gender: 'masculino',
    phone: '(11) 95432-1098',
    email: 'roberto.santos@email.com',
    address: {
      street: 'Rua Augusta',
      number: '500',
      neighborhood: 'Consolação',
      city: 'São Paulo',
      state: 'SP',
      zipCode: '01305-000'
    },
    emergencyContact: {
      name: 'Lucia Santos',
      relationship: 'Esposa',
      phone: '(11) 94321-0987'
    },
    medicalInfo: {
      allergies: ['Dipirona'],
      medications: ['Metformina 850mg'],
      conditions: ['Diabetes Tipo 2'],
      bloodType: 'B+'
    },
    status: 'ativo',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'system'
  }
];

export async function initializeDatabase() {
  try {
    console.log('Iniciando configuração do banco de dados...');

    // Verificar se já existem dados
    const usersSnapshot = await getDocs(collection(db, 'users'));
    if (!usersSnapshot.empty) {
      console.log('Banco de dados já possui dados. Pulando inicialização.');
      return;
    }

    // Aguardar um pouco para garantir que o Firebase está pronto
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Criar usuários iniciais
    console.log('Criando usuários iniciais...');
    for (const user of initialUsers) {
      await addDoc(collection(db, 'users'), user);
    }

    // Criar pacientes iniciais
    console.log('Criando pacientes iniciais...');
    for (const patient of initialPatients) {
      await addDoc(collection(db, 'patients'), patient);
    }

    // Criar algumas consultas de exemplo
    console.log('Criando agendamentos de exemplo...');
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const sampleAppointments: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>[] = [
      {
        patientId: 'patient1', // Será substituído pelos IDs reais
        professionalId: 'professional1',
        date: today.toISOString(),
        time: '09:00',
        duration: 30,
        type: 'consulta',
        specialty: 'Clínica Médica',
        status: 'agendado',
        observations: 'Consulta de rotina',
        createdBy: 'system'
      },
      {
        patientId: 'patient2',
        professionalId: 'professional2',
        date: tomorrow.toISOString(),
        time: '14:00',
        duration: 45,
        type: 'consulta',
        specialty: 'Psicologia',
        status: 'confirmado',
        observations: 'Primeira consulta',
        createdBy: 'system'
      }
    ];

    const now = new Date().toISOString();
    for (const appointmentData of sampleAppointments) {
      await addDoc(collection(db, 'appointments'), {
        ...appointmentData,
        createdAt: now,
        updatedAt: now
      });
    }

    // Criar algumas chamadas de exemplo para o painel
    console.log('Criando chamadas de exemplo...');
    const sampleCalls: CallPanel[] = [
      {
        id: 'call1',
        patientName: 'José da Silva',
        ticketNumber: '001',
        service: 'Clínica Médica',
        professional: 'Dra. Maria Santos',
        room: 'Consultório 1',
        status: 'chamando',
        calledAt: new Date().toISOString(),
        priority: 'normal'
      },
      {
        id: 'call2',
        patientName: 'Ana Carolina Souza',
        ticketNumber: '002',
        service: 'Psicologia',
        professional: 'Carlos Eduardo Lima',
        room: 'Consultório 2',
        status: 'atendendo',
        calledAt: new Date().toISOString(),
        priority: 'preferencial'
      }
    ];

    for (const callData of sampleCalls) {
      const { id, ...callWithoutId } = callData;
      await addDoc(collection(db, 'callPanel'), callWithoutId);
    }

    console.log('Banco de dados configurado com sucesso!');
    console.log('Usuários criados:');
    initialUsers.forEach(user => {
      console.log(`- ${user.name} (${user.email}) - ${user.role}`);
    });

  } catch (error) {
    console.error('Erro ao configurar banco de dados:', error);
    throw error;
  }
}